import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import '../../examination/styles/exam-variables.css';
import '../../examination/styles/examination.css';

// Fetch public tenant branding without auth
async function fetchBranding(slug) {
  if (!slug) return null;
  try {
    const res = await axios.get(`/public/tenant-branding/${slug}`);
    return res.data;
  } catch {
    return null;
  }
}

export default function ExamLoginPage() {
  const [slug, setSlug]         = useState(localStorage.getItem('tenantSlug') || '');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [branding, setBranding] = useState(null);
  const debounceRef             = useRef(null);
  const navigate                = useNavigate();
  const dispatch                = useDispatch();

  // Load branding whenever slug changes (debounced 600ms)
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!slug.trim()) { setBranding(null); return; }
    debounceRef.current = setTimeout(async () => {
      const data = await fetchBranding(slug.trim());
      setBranding(data?.name || data?.logo ? data : null);
    }, 600);
    return () => clearTimeout(debounceRef.current);
  }, [slug]);

  // If slug was pre-filled (returning user), load branding immediately
  useEffect(() => {
    if (slug) fetchBranding(slug).then((d) => setBranding(d?.name || d?.logo ? d : null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!slug.trim() || !email.trim() || !password) {
      setError('All fields are required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('username', email);
      params.append('password', password);
      const res = await axios.post(`/api/tenant/${slug}/exam/token`, params, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const { access_token } = res.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('examToken', access_token);
      localStorage.setItem('tenantSlug', slug);
      dispatch({ type: 'user/setToken', payload: { token: access_token, appContext: 'examination', tenant_slug: slug } });

      // Fetch and store user role, then redirect based on it
      let role = '';
      try {
        const meRes = await axios.get(`/api/tenant/${slug}/exam/me`, {
          headers: { Authorization: `Bearer ${access_token}` },
        });
        role = meRes.data?.role || '';
        localStorage.setItem('examUserRole', role);
      } catch { /* non-fatal — role stays empty, lands on admin routes guarded by ExamProtectedRoute */ }

      const isAdmin = role === 'tenant_admin' || role === 'coe' || role === 'system_admin';
      navigate(isAdmin ? '/examination/dashboard' : '/examination/faculty');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="exam-app" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--exam-bg)' }}>
      <div style={{ width: '100%', maxWidth: 420, padding: 24 }}>

        {/* Branding header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          {branding?.logo ? (
            /* Institution logo */
            <div style={{ marginBottom: 12 }}>
              <img
                src={branding.logo}
                alt={branding.name || 'Institution'}
                style={{ height: 56, maxWidth: 200, objectFit: 'contain', borderRadius: 6 }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          ) : (
            /* Default clipboard icon */
            <div style={{
              width: 52, height: 52, background: 'var(--exam-primary)',
              borderRadius: 'var(--exam-radius-md)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 12px',
            }}>
              <svg viewBox="0 0 24 24" fill="none" width="26" height="26">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                <rect x="9" y="3" width="6" height="4" rx="1" stroke="white" strokeWidth="2"/>
                <path d="M9 12h6M9 16h4" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
          )}

          {branding?.name ? (
            <div>
              <div style={{ fontSize: 13, color: 'var(--exam-text-muted)', marginBottom: 4 }}>{branding.name}</div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--exam-text)', letterSpacing: '-0.03em' }}>
                Examination<span style={{ color: 'var(--exam-primary)' }}>OS</span>
              </h1>
            </div>
          ) : (
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--exam-text)', letterSpacing: '-0.03em' }}>
              Examination<span style={{ color: 'var(--exam-primary)' }}>OS</span>
            </h1>
          )}

          <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--exam-text-muted)' }}>
            Sign in to your examination workspace
          </p>
        </div>

        <form onSubmit={handleLogin} className="exam-card">
          <div className="exam-field">
            <label className="exam-label required">Institution Code</label>
            <input
              className="exam-input"
              placeholder="your-institution"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/\s/g, '-'))}
              autoFocus={!slug}
              autoComplete="organization"
            />
          </div>
          <div className="exam-field">
            <label className="exam-label required">Email</label>
            <input
              className="exam-input"
              type="email"
              placeholder="faculty@institution.edu"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>
          <div className="exam-field" style={{ marginBottom: 0 }}>
            <label className="exam-label required">Password</label>
            <input
              className="exam-input"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div style={{
              marginTop: 14, padding: '10px 14px',
              background: 'var(--exam-error-light)', color: 'var(--exam-error)',
              borderRadius: 'var(--exam-radius-sm)', fontSize: 13,
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            className="exam-btn exam-btn-primary exam-btn-lg"
            style={{ width: '100%', marginTop: 20, justifyContent: 'center' }}
            disabled={loading}
          >
            {loading && <span className="exam-spinner" style={{ width: 14, height: 14 }} />}
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 12.5, color: 'var(--exam-text-muted)' }}>
          Part of the{' '}
          <Link to="/login" style={{ color: 'var(--exam-primary)', textDecoration: 'none', fontWeight: 500 }}>
            AcademicOS
          </Link>{' '}
          platform
        </p>
      </div>
    </div>
  );
}
